import { useCallback, useEffect, useRef, useState } from "react";
import { Package, Sparkle, Camera, CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));

export const Parcels = ({ rides = [], mates = [], me, onChange }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    kind: "small-parcel", what: "", from_place: "", to_place: "", by_when: "", for_whom: "",
    miles: 12, ride_id: "", free: false, colleagues_only: false, public: true,
  });
  const [quote, setQuote] = useState(null);
  const [matches, setMatches] = useState(null);
  const [share, setShare] = useState([]);
  const fileRef = useRef(null);
  const [dropping, setDropping] = useState(null);

  const load = useCallback(() => {
    api.get("/rides/parcels").then(({ data: d }) => setData(d)).catch(oops);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.post("/rides/parcels/quote", {
      kind: form.kind, miles: Number(form.miles) || 0, free: form.free,
    }).then(({ data: d }) => setQuote(d)).catch(() => setQuote(null));
  }, [form.kind, form.miles, form.free]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); onChange?.(); }
    catch (e) { oops(e); } finally { setBusy(false); }
  };

  const dropWithPhoto = async (parcel, file) => {
    setBusy(true);
    try {
      let photo_id = "";
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("caption", `Dropped at ${parcel.to_place}`);
        const { data: img } = await api.post("/images", fd);
        photo_id = img.id;
      }
      await api.post(`/rides/parcels/${parcel.id}/dropped`, { photo_id, signed_by: "" });
      toast.success("Dropped off — the sender has been told");
      load(); onChange?.();
    } catch (e) { oops(e); } finally { setBusy(false); setDropping(null); }
  };

  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="parcels-box">
      <div className="border border-border bg-white p-4" data-testid="parcel-post-box">
        <p className="overline text-slate-500">
          <Package size={13} className="mr-1 inline align-[-2px] text-[#B3241B]" />
          Send a letter or parcel with someone going that way
        </p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <select data-testid="parcel-kind" value={form.kind} aria-label="What is it"
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            {data.kinds.map((k) => <option key={k.key} value={k.key}>{k.name}</option>)}
          </select>
          <Input data-testid="parcel-what" placeholder="What is it — 'signed job sheets'"
            value={form.what} onChange={(e) => setForm({ ...form, what: e.target.value })} />
          <Input data-testid="parcel-from" placeholder="Pick up from"
            value={form.from_place} onChange={(e) => setForm({ ...form, from_place: e.target.value })} />
          <Input data-testid="parcel-to" placeholder="Drop off at"
            value={form.to_place} onChange={(e) => setForm({ ...form, to_place: e.target.value })} />
          <Input data-testid="parcel-when" placeholder="By when (optional)"
            value={form.by_when} onChange={(e) => setForm({ ...form, by_when: e.target.value })} />
          <Input data-testid="parcel-for" placeholder="Who is it for (optional)"
            value={form.for_whom} onChange={(e) => setForm({ ...form, for_whom: e.target.value })} />
          <label className="flex items-center gap-2 text-sm text-slate-600">
            Miles
            <Input data-testid="parcel-miles" type="number" min={0} value={form.miles}
              onChange={(e) => setForm({ ...form, miles: e.target.value })} className="w-24" />
          </label>
          <select data-testid="parcel-ride" value={form.ride_id} aria-label="Put it on a run"
            onChange={(e) => setForm({ ...form, ride_id: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            <option value="">Any run going that way</option>
            {rides.filter((r) => r.status === "open").map((r) => (
              <option key={r.id} value={r.id}>{r.owner_name} · {r.from_place} → {r.to_place}</option>
            ))}
          </select>
        </div>

        <div className="mt-2 flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" data-testid="parcel-free" checked={form.free}
              onChange={(e) => setForm({ ...form, free: e.target.checked })} />
            No charge — mates' favour
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" data-testid="parcel-colleagues" checked={form.colleagues_only}
              onChange={(e) => setForm({ ...form, colleagues_only: e.target.checked })} />
            Colleagues only
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" data-testid="parcel-public" checked={form.public}
              onChange={(e) => setForm({ ...form, public: e.target.checked })} />
            Show on the public board
          </label>
        </div>

        <div className="mt-3 flex flex-wrap gap-2" data-testid="parcel-mates">
          {mates.slice(0, 10).map((m) => {
            const on = share.includes(m.id);
            return (
              <button key={m.id} type="button" data-testid={`parcel-mate-${m.id}`}
                onClick={() => setShare(on ? share.filter((i) => i !== m.id) : [...share, m.id])}
                className={`tap-safe border px-3 py-2 text-xs font-semibold ${
                  on ? "border-[#B3241B] bg-[#B3241B] text-white"
                    : "border-border bg-white text-slate-700 hover:border-[#B3241B]"}`}>
                {m.name}
              </button>
            );
          })}
        </div>

        {quote && (
          <p className="mt-3 text-sm text-slate-700" data-testid="parcel-quote">
            {quote.kind_name} over {quote.miles} miles —{" "}
            <span className="font-semibold text-[#0F172A]">
              {quote.free ? "no charge" : `${quote.symbol}${Number(quote.fee).toFixed(2)} to the driver`}
            </span>
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" data-testid="parcel-post" disabled={busy || form.what.trim().length < 2}
            onClick={() => act(async () => {
              await api.post("/rides/parcels", {
                ...form, miles: Number(form.miles) || 0, share_with: share,
              });
              setForm({ ...form, what: "", from_place: "", to_place: "" });
              setShare([]);
            }, "On the board — your folks have been told")}>
            Put it on the board
          </Button>
          <Button size="sm" variant="outline" data-testid="parcel-match" disabled={busy}
            onClick={() => act(async () => {
              const { data: d } = await api.post("/rides/parcels/match", {
                from_place: form.from_place, to_place: form.to_place,
                by_when: form.by_when, kind: form.kind });
              setMatches(d);
            }, "Had a look round")}>
            <Sparkle size={14} className="mr-1.5" /> Who is driving past?
          </Button>
        </div>

        {matches && (
          <div className="mt-3 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2" data-testid="parcel-matches">
            <p className="text-sm text-[#0F172A]">{matches.line}</p>
            <ul className="mt-1 space-y-1 text-sm text-slate-700">
              {matches.matches.map((m) => (
                <li key={m.ride_id} data-testid={`parcel-match-${m.ride_id}`}>
                  <span className="font-semibold text-[#0F172A]">{m.fit}</span> · {m.why}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div data-testid="parcel-list">
        <p className="overline text-slate-500">Letters and parcels on the move</p>
        <ul className="mt-2 space-y-2">
          {data.parcels.map((p) => {
            const isSender = p.sender_id === me?.id;
            const isCarrier = p.carrier_id === me?.id;
            return (
              <li key={p.id} className="border border-border bg-white p-3" data-testid={`parcel-${p.id}`}>
                <p className="text-sm font-semibold text-[#0F172A]">
                  {p.what} · {p.kind_name}
                </p>
                <p className="text-xs text-slate-600">
                  {p.from_place} → {p.to_place}
                  {p.by_when ? ` · by ${p.by_when}` : ""} · {p.sender_name}
                  {p.carrier_name ? ` · carried by ${p.carrier_name}` : " · waiting for a driver"}
                  {p.fee?.free ? " · no charge" : ` · ${p.symbol}${Number(p.fee?.fee || 0).toFixed(2)}`}
                </p>
                <p className="mt-1 text-xs">
                  <span className={p.dropped ? "font-semibold text-[#0B5C2E]" : "text-slate-500"}>
                    {p.status}
                  </span>
                  {p.signed_by ? ` · signed for by ${p.signed_by}` : ""}
                  {p.dropped_at ? ` · ${p.dropped_at.slice(11, 16)}` : ""}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {!isSender && !p.carrier_id && (
                    <Button size="sm" variant="outline" data-testid={`parcel-take-${p.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/rides/parcels/${p.id}/take`), "You are carrying it")}>
                      I am going that way
                    </Button>
                  )}
                  {isCarrier && !p.picked_up && (
                    <Button size="sm" variant="outline" data-testid={`parcel-pickup-${p.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/rides/parcels/${p.id}/picked-up`, {}), "Picked up")}>
                      Picked it up
                    </Button>
                  )}
                  {isCarrier && p.picked_up && !p.dropped && (
                    <>
                      <Button size="sm" data-testid={`parcel-drop-${p.id}`} disabled={busy}
                        onClick={() => act(() => api.post(`/rides/parcels/${p.id}/dropped`, {}), "Dropped off")}>
                        <CheckCircle size={14} className="mr-1.5" /> Dropped it off
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`parcel-photo-${p.id}`} disabled={busy}
                        onClick={() => { setDropping(p); fileRef.current?.click(); }}>
                        <Camera size={14} className="mr-1.5" /> Drop with a photo
                      </Button>
                    </>
                  )}
                  {isSender && !p.dropped && (
                    <Button size="sm" variant="outline" data-testid={`parcel-cancel-${p.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/rides/parcels/${p.id}/cancel`), "Called off")}>
                      Call it off
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
          {data.parcels.length === 0 && (
            <li className="text-sm text-slate-500">Nothing waiting for a lift just now.</li>
          )}
        </ul>
        <input ref={fileRef} type="file" accept="image/*" className="hidden"
          data-testid="parcel-photo-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file && dropping) dropWithPhoto(dropping, file);
            e.target.value = "";
          }} />
      </div>

      <InviteBox />
    </div>
  );
};

const InviteBox = () => {
  const [invite, setInvite] = useState(null);
  return (
    <div className="border border-border bg-white p-4" data-testid="parcel-invite-box">
      <p className="overline text-slate-500">
        <PaperPlaneTilt size={13} className="mr-1 inline align-[-2px]" /> Bring your own folks in
      </p>
      <p className="mt-1 text-sm text-slate-600">
        Pass this round on Facebook, WhatsApp or the crew chat — anyone who taps it lands on the
        rides board and can take a seat or carry a parcel.
      </p>
      <Button className="mt-2" size="sm" variant="outline" data-testid="parcel-invite"
        onClick={async () => {
          try {
            const { data } = await api.post("/rides/invite");
            setInvite(data);
            await navigator.clipboard.writeText(`${data.text} ${data.url}`).catch(() => {});
            toast.success("Invite copied — paste it wherever your folks are");
          } catch (e) { oops(e); }
        }}>
        Get my invite link
      </Button>
      {invite && (
        <p className="mt-2 break-all text-xs text-slate-500" data-testid="parcel-invite-link">
          {invite.url} · {invite.joined} joined so far
        </p>
      )}
    </div>
  );
};
